import { useSetAtom } from 'jotai'
import { ScopeProvider } from 'jotai-scope'
import { FC, PropsWithChildren, useLayoutEffect } from 'react'

import { simulationStateAtom } from './simulation.atoms'
import {
  useAutomationSimulation,
  type UseAutomationSimulationProps
} from './useAutomationSimulation'

/**
 * Runs one simulation and hands the result down to anything below it through
 * `useAutomationSimulationContext`. The atom is scoped to this provider, so two
 * of these on the same page won't tread on each other.
 */
export const AutomationSimulationProvider: FC<
  PropsWithChildren<UseAutomationSimulationProps>
> = ({ children, ...simulationProps }) => (
  <ScopeProvider atoms={[simulationStateAtom]}>
    <SimulationState {...simulationProps}>{children}</SimulationState>
  </ScopeProvider>
)

const SimulationState: FC<PropsWithChildren<UseAutomationSimulationProps>> = ({
  children,
  ...simulationProps
}) => {
  const simulation = useAutomationSimulation(simulationProps)
  const setSimulationState = useSetAtom(simulationStateAtom)

  // This has to be a layout effect. A normal one runs after the browser has
  // drawn, so everything below would show the previous result for a frame.
  //
  // Passing the value straight to the provider as `atoms={[[atom, value]]}`
  // doesn't work either: jotai-scope only reads that tuple once when the store
  // is set up, so the simulation would be stuck on its first result forever.
  useLayoutEffect(() => {
    setSimulationState(simulation)
  }, [simulation, setSimulationState])

  return <>{children}</>
}
